import {Component, OnInit} from '@angular/core';
import {UserCardService} from "../../service/user-card.service";
import {HttpErrorResponse} from "@angular/common/http";
import {ActivatedRoute, NavigationExtras, Params, Router} from "@angular/router";
import {UserCardResponse} from "../../interface/userCardResponse";
import {ImageType} from "../../enums/image-type-enum";
import {UserService} from "../../service/user.service";
import {UserResponse} from "../../interface/userResponse";
import {
  CardsByQrManageRatingNavigationExtrasModel
} from "./cards-by-qr-manage-rating/cards-by-qr-manage-rating-navigation-extras.model";
import {UserCardReviewService} from "../../service/user-card-review.service";
import {UserCardReviewResponse} from "../../models/userCardReviewResponse";
import {DEFAULT_USER_ICON} from "../../constants/global.constants";
import {MockupDataModel} from "../../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../../qr-copy-message/snack-bar-util.service";
import {CardsByQrRatingCommonService} from "./cards-by-qr-rating-common.service";
import {NavigationService} from "../../service/navigation.service";
import {TranslateService} from "@ngx-translate/core";
import {Language} from "../../models/language";

@Component({
  selector: 'app-cards-by-qr-rating',
  templateUrl: './cards-by-qr-rating.component.html',
  styleUrl: './cards-by-qr-rating.component.scss'
})
export class CardsByQrRatingComponent implements OnInit {

  public static readonly QR_CODE_PARAM: string = 'qrCode';
  private static readonly REVIEW_DESCRIPTION_LENGTH: number = 189;

  protected userCard!: UserCardResponse;
  protected companyLogoExists: boolean = false;
  protected companyLogo: string | null = null;
  protected profilePicture: string | null = null;
  protected reviews: Array<UserCardReviewResponse> = [];
  protected currentUser: UserResponse | null = null;
  protected isCardBelongsToCurrentUser: boolean = false;

  private isThereCurrentUser: boolean = false;

  constructor(
    private userService: UserService,
    private userCardService: UserCardService,
    private userCardReviewService: UserCardReviewService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: SnackBarUtil,
    private cardsByQrRatingCommonService: CardsByQrRatingCommonService,
    private navigationService: NavigationService,
    private translateService: TranslateService
  ) {
  }

  ngOnInit() {
    this.initCurrentUser();
    this.initUserCard();
    this.initLanguage();
  }

  get isLeaveReviewButtonDisabled() {
    return this.reviews.filter(review => review.sender.id == this.currentUser?.id).length >= 1;
  }

  protected isReviewBelongsToCurrentUser(review: UserCardReviewResponse): boolean {
    return this.isThereCurrentUser && this.currentUser!.id === review.sender.id;
  }

  protected isCreateReviewButtonVisible(): boolean {
    return !this.isCardBelongsToCurrentUser && this.isThereCurrentUser;
  }

  protected getReviewDescription(review: UserCardReviewResponse): string {
    if (!review.isShowMoreClicked && review.reviewText.length >= CardsByQrRatingComponent.REVIEW_DESCRIPTION_LENGTH) {
      return review.reviewText.substring(0, CardsByQrRatingComponent.REVIEW_DESCRIPTION_LENGTH).trim() + '...';
    }
    return review.reviewText;
  }

  protected getCompanyInformation(): string {
    if (!this.userCard || !this.userCard.specialization) return '';
    const transformedSpecialization = this.userCard.specialization ? this.userCard.specialization + ' at ' : '';
    return transformedSpecialization + this.userCard.company;
  }

  protected getFullName(): string {
    if (!this.userCard) return '';
    return this.userCard.name + ' ' + this.userCard.surname;
  }

  protected backToCard() {
    this.router.navigateByUrl(this.userCard.cardId).catch();
  }

  protected redirectToManageReviewPage(review?: UserCardReviewResponse): void {
    if (!this.currentUser) {
      throw Error('Current user is not defined');
    }
    const data: CardsByQrManageRatingNavigationExtrasModel = {
      review: review ? review : null,
      currentUser: this.currentUser,
      userCardProfilePicture: this.profilePicture,
      companyLogoExists: this.companyLogoExists,
      companyLogo: this.companyLogo,
      userCardFullName: this.getFullName(),
      userCardSpecialization: this.getCompanyInformation(),
      userCardId: this.userCard.id
    }
    const navigationExtras: NavigationExtras = {
      state: {data: data}
    }
    this.router.navigate([`${this.userCard.cardId}/rating/manage`], navigationExtras).catch();
  }

  protected redirectToLoginPage(route: string): void {
    const returnUrl = `/${this.userCard.cardId}/rating`;
    this.navigationService.navigateToLoginWithReturnUrl(route, returnUrl)
  }

  protected onClickDeleteReview(reviewId: bigint): void {
    this.cardsByQrRatingCommonService.onClickDeleteReview(() => this.deleteReviewById(reviewId));
  }

  handleReviewButtonClick(): void {
    if (this.isCreateReviewButtonVisible()) {
      this.redirectToManageReviewPage();
    } else {
      this.redirectToLoginPage('/login');
    }
  }

  private initUserCard() {
    this.route.params.subscribe({
      next: (params: Params) => {
        const qrCode = params[CardsByQrRatingComponent.QR_CODE_PARAM];
        if (qrCode) {
          this.getCardByQrCode(qrCode);
        } else {
          this.navigateToErrorPageWithError();
        }
      }
    });
  }

  private getCardByQrCode(qrCode: string) {
    this.userCardService.getCardByQRCode(qrCode).subscribe({
      next: (userCardResponse: UserCardResponse) => {
        this.userCard = userCardResponse;
        this.initUserCardPictures(userCardResponse.id);
        this.initReviews();
        this.initRatingRelatedConditions(this.userCard.userId);
      },
      error: (err: HttpErrorResponse) => {
        this.navigateToErrorPageWithError(err);
      }
    });
  }

  private initReviews(): void {
    this.userCardReviewService.getReviewsByCardId(this.userCard.id).subscribe({
      next: (reviews: Array<UserCardReviewResponse>) => {
        this.reviews = reviews;
        this.reviews.forEach(review => {
          review.isShowMoreClicked = false;
          review.profilePictureUrl = DEFAULT_USER_ICON;
          this.getReviewerProfilePictureFromAPI(review);
        })
      },
      error: (err: HttpErrorResponse) => {
        this.router.navigate(['/error-page']).catch();
        throw new Error('Error fetching reviews:', err.error.message);
      }
    });
  }

  private initRatingRelatedConditions(userCardUserId: bigint) {
    this.isThereCurrentUser = !!(this.currentUser);
    this.isCardBelongsToCurrentUser = this.isThereCurrentUser ? this.currentUser!.id == userCardUserId : false;
  }

  private initUserCardPictures(id: bigint) {
    this.initCompanyLogo(id);
    this.profilePicture = this.userCardService.getCardImage(id, ImageType.PROFILE_PIC);
  }

  private initCompanyLogo(id: bigint) {
    this.companyLogo = this.userCardService.getCardImage(id, ImageType.COMPANY_LOGO);
    this.userCardService.checkImageExists(this.companyLogo).subscribe({
      next: (exists) => {
        this.companyLogoExists = exists;
      }
    });
  }

  private initCurrentUser() {
    let email = this.userService.email;
    this.userService.findByEmail(email).subscribe({
      next: (userResponse: UserResponse) => {
        this.currentUser = userResponse;
      }
    })
  }

  private getReviewerProfilePictureFromAPI(review: UserCardReviewResponse) {
    this.userService.getProfileImage(review.sender.id).subscribe({
      next: (imageData) => {
        const fileReader = new FileReader();
        fileReader.onload = (event) => {
          review.profilePictureUrl = event.target?.result as string;
        };
        fileReader.readAsDataURL(imageData);
      },
      error: _ => {
        review.profilePictureUrl = DEFAULT_USER_ICON;
      }
    });
  }

  private deleteReviewById(reviewId: bigint): void {
    this.userCardReviewService.deleteReviewById(reviewId).subscribe({
      next: () => {
        this.reviews = this.reviews.filter(review => review.id !== reviewId);
        this.translateService.get('rating.review_deleted').subscribe({
          next: (message: string) => {
            this.showSnackbar(MockupDataModel.success(message), SnackBarUtil.CLASSES.success);
          }
        });
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigateByUrl(currentUrl);
        });
        },
      error: (err: HttpErrorResponse) => {
        this.translateService.get('rating.cannot_delete_review').subscribe({
          next: (message: string) => {
            this.showSnackbar(MockupDataModel.error(message), SnackBarUtil.CLASSES.error);
          }
        });
        throw new Error('Error deleting review: ' + (err.error.message || 'Unknown error'));
      }
    })
  }

  private navigateToErrorPageWithError(err?: HttpErrorResponse) {
    this.router.navigate(['/error-page']).then(() => {
      let errorMessage: string = err ? err.message : 'Unknown error';
      let message: string = 'Error fetching card by QR code: ' + errorMessage;
      throw new Error(message);
    });
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data,
    });
  }

  protected REVIEW_DESCRIPTION_LENGTH = CardsByQrRatingComponent.REVIEW_DESCRIPTION_LENGTH;

  private initLanguage(): void {
    if (!this.translateService.currentLang) {
      this.translateService.setDefaultLang(Language.HY);
      this.translateService.use(Language.HY);
    }
  }
}
